/**
 * SpriteAtlas — pure, framework-free sprite manifest registry.
 *
 * Deliberately imports NOTHING (no Phaser) so it is fully unit-testable in
 * a node environment. The Phaser loading adapter lives in BootScene.ts.
 *
 * manifest.json format:
 *   { "<assetId>": { "file": "sprites/<name>.png", "w": number, "h": number } }
 *
 * Usage (BootScene preload):
 *   const atlas = new SpriteAtlas();
 *   atlas.loadManifest(manifest);
 *   // register each assetId → Phaser texture key after Phaser loads the image
 *   atlas.register('player_idle');
 *   atlas.getTexture('player_idle'); // → 'player_idle'
 *   atlas.getTexture('unknown');     // → null
 */

export interface SpriteEntry {
  file: string;
  w: number;
  h: number;
}

export type SpriteManifest = Record<string, SpriteEntry>;

export class SpriteAtlas {
  private readonly manifest: SpriteManifest = {};
  private readonly loaded = new Map<string, string>();

  /** Ingest the raw manifest object (parsed from manifest.json). */
  loadManifest(data: SpriteManifest): void {
    for (const [id, entry] of Object.entries(data)) {
      this.manifest[id] = entry;
    }
  }

  /** All assetIds declared in the manifest. */
  get assetIds(): readonly string[] {
    return Object.keys(this.manifest);
  }

  /** The SpriteEntry for a given assetId, or undefined if not in manifest. */
  getEntry(assetId: string): SpriteEntry | undefined {
    return this.manifest[assetId];
  }

  /**
   * Mark an assetId as successfully loaded (called by the Phaser adapter after
   * the texture is confirmed in the cache). The texture key equals the assetId.
   */
  register(assetId: string): void {
    if (this.manifest[assetId] !== undefined) {
      this.loaded.set(assetId, assetId);
    }
  }

  /**
   * Returns the Phaser texture key for a loaded assetId, or null when the
   * texture is missing or failed to load.
   */
  getTexture(assetId: string): string | null {
    return this.loaded.get(assetId) ?? null;
  }

  /** True when at least one texture has been registered (handy for boot checks). */
  get hasAny(): boolean {
    return this.loaded.size > 0;
  }
}

/** Singleton used across scenes. */
export const spriteAtlas = new SpriteAtlas();
